/**
 * Hotel Filter Count Slice
 *
 * Manages filter counts for hotel search (price buckets, star ratings, amenities, etc.).
 * Called during hotel search to populate filter options with counts.
 */
import { createSlice } from '@reduxjs/toolkit';
import type { FilterCategory } from '@contracts';
import { fetchFilterCountThunk } from '../thunks/filterCountThunk';

// ============================================
// Types
// ============================================

export interface FilterCountState {
  readonly data: FilterCategory[] | null;
  readonly isLoading: boolean;
  readonly error: string | null;
}

// ============================================
// Initial State
// ============================================

export const initialFilterCountState: FilterCountState = {
  data: null,
  isLoading: false,
  error: null,
};

// ============================================
// Slice
// ============================================

const filterCountSlice = createSlice({
  name: 'hotels/filterCount',
  initialState: initialFilterCountState,
  reducers: {
    clearFilterCount: (state) => {
      state.data = null;
      state.error = null;
    },
    resetFilterCountState: () => initialFilterCountState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFilterCountThunk.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFilterCountThunk.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload;
      })
      .addCase(fetchFilterCountThunk.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Failed to fetch filter counts';
      });
  },
});

// ============================================
// Actions
// ============================================

export const { clearFilterCount, resetFilterCountState } = filterCountSlice.actions;

// ============================================
// Reducer Export
// ============================================

export const filterCountReducer = filterCountSlice.reducer;
